import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import U2000ComboBoxBase from "../../../u2000Core/components/formControls/combobox/U2000ComboBoxBase";
import U2000CheckBoxBase from "../../../u2000Core/components/formControls/checkbox/U2000CheckBoxBase";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import { NS, u3125FormatCurrency } from "../../U3125";
import U3125ForfaitWizardInvoiceAmountInput from "./U3125ForfaitWizardInvoiceAmountInput";
import {
  pickInvoiceTypeLabel,
  u3125FilterStandardBillingInvoiceTypes,
  u3125ForfaitWizardPreviewLineIsOk,
  u3125ForfaitWizardLineIsBillingComplete,
  u3125ForfaitWizardStatusBadgeClass,
  u3125ForfaitWizardSuggestInvoiceTypeCode,
  type U3125ForfaitWizardEditableLine,
  type U3125ForfaitWizardPreviewLine,
  type U3125ForfaitWizardPreviewLineStatus,
  type U3125InvoiceTypeInitItem,
} from "../../U3125Service";

export type U3125ForfaitWizardPreviewGridProps = {
  previewLines: U3125ForfaitWizardPreviewLine[];
  editableLines: U3125ForfaitWizardEditableLine[];
  invoiceTypes: U3125InvoiceTypeInitItem[];
  i18nLanguage: string;
  onEditableLinesChange: (lines: U3125ForfaitWizardEditableLine[]) => void;
  onCommit: () => void;
  commitLabel: string;
  committing: boolean;
  /** Colonnes détail TEC (compteur + montant TEC) — requiert `U3125:VIEWTECLINES` (BL-0618). */
  showTecDetailColumns?: boolean;
  /** BL-0654 — si true, pas de plafond `maxValue` sur le montant facture. */
  allowInvoiceAboveBudget?: boolean;
};

function statusLabelKey(status: U3125ForfaitWizardPreviewLineStatus): string {
  switch (status) {
    case "ok":
      return "wizardStatusOk";
    case "not_found":
      return "wizardStatusNotFound";
    case "not_fixed_price":
      return "wizardStatusNotFixedPrice";
    case "not_billable":
      return "wizardStatusNotBillable";
    case "draft_invoice_exists":
      return "wizardStatusDraftExists";
    case "billing_complete":
      return "wizardStatusDone";
    default:
      return "wizardStatusOk";
  }
}

const U3125ForfaitWizardPreviewGrid: React.FC<U3125ForfaitWizardPreviewGridProps> = ({
  previewLines,
  editableLines,
  invoiceTypes,
  i18nLanguage,
  onEditableLinesChange,
  onCommit,
  commitLabel,
  committing,
  showTecDetailColumns = true,
  allowInvoiceAboveBudget = false,
}) => {
  const { t } = useTranslation();
  const billingTypes = useMemo(() => u3125FilterStandardBillingInvoiceTypes(invoiceTypes), [invoiceTypes]);
  const comboData = useMemo(
    () =>
      billingTypes.map((it) => ({
        key: it.code,
        description: `${pickInvoiceTypeLabel(invoiceTypes, it.code, i18nLanguage)} (${it.code})`,
      })),
    [billingTypes, invoiceTypes, i18nLanguage],
  );

  const editableByProjectId = useMemo(() => new Map(editableLines.map((l) => [l.projectId, l])), [editableLines]);

  const updateEditable = (projectId: string, patch: Partial<U3125ForfaitWizardEditableLine>) => {
    onEditableLinesChange(editableLines.map((line) => (line.projectId === projectId ? { ...line, ...patch } : line)));
  };

  const nbOk = editableLines.filter((l) => l.included).length;
  const nbError = previewLines.filter((line) => {
    if (!u3125ForfaitWizardPreviewLineIsOk(line)) return true;
    const edited = editableByProjectId.get(line.projectId);
    return u3125ForfaitWizardLineIsBillingComplete(line, edited);
  }).length;
  const totalSaisi = editableLines.filter((l) => l.included).reduce((sum, l) => sum + (Number(l.invoiceAmount) || 0), 0);

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-800">
        {t("wizardSummaryBanner", {
          ns: NS,
          ok: nbOk,
          errors: nbError,
          total: u3125FormatCurrency(totalSaisi),
        })}
      </div>

      <div className="w-full min-w-0 overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("lblProjectNumber", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("lblProjetName", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("wizardColCustomer", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("lblInvoiceStatus", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("wizardColMessage", { ns: NS })}</th>
              {showTecDetailColumns ? (
                <th className="px-3 py-2 text-right font-medium text-gray-700">{t("wizardColWipCount", { ns: NS })}</th>
              ) : null}
              {showTecDetailColumns ? (
                <th className="px-3 py-2 text-right font-medium text-gray-700">{t("wizardColTecAmount", { ns: NS })}</th>
              ) : null}
              <th className="px-3 py-2 text-right font-medium text-gray-700">{t("wizardColAlreadyInvoiced", { ns: NS })}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">{t("wizardColFixedAmount", { ns: NS })}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">{t("wizardColRemainingBudget", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("wizardColInvoiceType", { ns: NS })}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">{t("wizardColInvoiceAmount", { ns: NS })}</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">{t("wizardColInclude", { ns: NS })}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {previewLines.map((line) => {
              const isOk = u3125ForfaitWizardPreviewLineIsOk(line);
              const edited = isOk ? editableByProjectId.get(line.projectId) : undefined;
              const isBillingComplete = isOk && u3125ForfaitWizardLineIsBillingComplete(line, edited);
              const isBillable = isOk && !isBillingComplete;
              const displayStatus: U3125ForfaitWizardPreviewLineStatus = isBillingComplete
                ? "billing_complete"
                : isOk
                  ? "ok"
                  : line.status;
              const rowMuted = !isOk || isBillingComplete;
              const rowClassName = isBillingComplete ? "bg-slate-50/80" : rowMuted ? "bg-red-50/40" : undefined;
              const maxAmount =
                isBillable && !allowInvoiceAboveBudget ? (line.remainingBudget ?? 0) + 0.01 : isBillable ? undefined : 0;

              return (
                <tr key={line.inputProjectNumber} className={rowClassName}>
                  <td className="whitespace-nowrap px-3 py-2 font-medium">{line.inputProjectNumber}</td>
                  <td className="px-3 py-2">{isOk ? line.projetName ?? "—" : "—"}</td>
                  <td className="px-3 py-2">{isOk ? line.customer?.customerName ?? "—" : "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${u3125ForfaitWizardStatusBadgeClass(displayStatus)}`}>
                      {t(statusLabelKey(displayStatus), { ns: NS })}
                    </span>
                  </td>
                  <td className="max-w-xs px-3 py-2 text-gray-600">
                    {isBillingComplete ? t("wizardMsgBillingComplete", { ns: NS }) : !isOk ? line.errorMessage ?? "—" : "—"}
                  </td>
                  {showTecDetailColumns ? (
                    <td className="px-3 py-2 text-right" title={t("wizardColWipCountTooltip", { ns: NS })}>
                      {isOk ? line.workInProgressLineCount ?? 0 : "—"}
                    </td>
                  ) : null}
                  {showTecDetailColumns ? (
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {isOk ? u3125FormatCurrency(line.amountTecToInvoice) : "—"}
                    </td>
                  ) : null}
                  <td className="whitespace-nowrap px-3 py-2 text-right">{isOk ? u3125FormatCurrency(line.amountAlreadyInvoiced) : "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">{isOk ? u3125FormatCurrency(line.fixedAmount) : "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">{isOk ? u3125FormatCurrency(line.remainingBudget) : "—"}</td>
                  <td className="min-w-40 px-3 py-2">
                    {isBillable && edited ? (
                      <U2000ComboBoxBase
                        data={comboData}
                        keyField="key"
                        descField="description"
                        value={comboData.find((d) => d.key === edited.invoiceTypeCode) ?? null}
                        onChange={(key) => updateEditable(line.projectId, { invoiceTypeCode: key === "FF" ? "FF" : "FP" })}
                        disabled={!edited.included || comboData.length === 0}
                        placeHolder={t("lblInvoiceTypeCode", { ns: NS })}
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isBillable && edited ? (
                      <U3125ForfaitWizardInvoiceAmountInput
                        key={line.projectId}
                        value={edited.invoiceAmount}
                        disabled={!edited.included}
                        maxValue={maxAmount}
                        onChange={(amount) =>
                          updateEditable(line.projectId, {
                            invoiceAmount: amount,
                            invoiceTypeCode: u3125ForfaitWizardSuggestInvoiceTypeCode(amount, line.remainingBudget ?? 0),
                            included: amount > 0.005 ? edited.included : false,
                          })
                        }
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2 align-middle text-center">
                    {isBillable && edited ? (
                      <U2000CheckBoxBase
                        fieldName={`wizard-include-${line.projectId}`}
                        label=""
                        value={edited.included}
                        setValue={(included) => updateEditable(line.projectId, { included })}
                        className="inline-flex justify-center gap-0 [&_label:last-of-type:empty]:hidden"
                      />
                    ) : (
                      <U2000CheckBoxBase
                        fieldName={`wizard-include-disabled-${line.inputProjectNumber}`}
                        label=""
                        value={false}
                        readonly
                        className="inline-flex justify-center gap-0 [&_label:last-of-type:empty]:hidden"
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <U2000ActionBtn label={commitLabel} handleClick={onCommit} disabled={committing || nbOk === 0} />
      </div>
    </div>
  );
};

export default U3125ForfaitWizardPreviewGrid;
